import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';

// Animation Hook
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

export default function About() {
  const { t } = useTranslation();
  const visibleElements = useScrollAnimation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getAnimationClass = (elementId: string, baseClass = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  const milestones = [
    {
      year: '2015',
      title: t('milestone_2010_title'),
      description: t('milestone_2010_desc'),
      icon: 'ri-building-line',
      color: 'blue'
    },
    {
      year: '2017',
      title: t('milestone_2012_title'),
      description: t('milestone_2012_desc'),
      icon: 'ri-lightbulb-line',
      color: 'teal'
    },
    {
      year: '2019',
      title: t('milestone_2015_title'),
      description: t('milestone_2015_desc'),
      icon: 'ri-line-chart-line',
      color: 'cyan'
    },
    {
      year: '2021',
      title: t('milestone_2018_title'),
      description: t('milestone_2018_desc'),
      icon: 'ri-award-line',
      color: 'red'
    },
    {
      year: '2022',
      title: t('milestone_2020_title'),
      description: t('milestone_2020_desc'),
      icon: 'ri-robot-line',
      color: 'orange'
    },
    {
      year: '2023',
      title: t('milestone_2023_title'),
      description: t('milestone_2023_desc'),
      icon: 'ri-leaf-line',
      color: 'green'
    },
    {
      year: '2024',
      title: t('milestone_2024_title'),
      description: t('milestone_2024_desc'),
      icon: 'ri-rocket-line',
      color: 'purple'
    }
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: t('value_1_title'),
      description: t('value_1_desc'),
      color: 'blue'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('value_2_title'),
      description: t('value_2_desc'),
      color: 'teal'
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: t('value_3_title'),
      description: t('value_3_desc'),
      color: 'cyan'
    },
    {
      icon: 'ri-team-line',
      title: t('value_4_title'),
      description: t('value_4_desc'),
      color: 'red'
    }
  ];

  const stats = [
    { number: t('stat_1_number'), label: t('stat_1_label'), icon: 'ri-time-line' },
    { number: t('stat_2_number'), label: t('stat_2_label'), icon: 'ri-file-list-3-line' },
    { number: t('stat_3_number'), label: t('stat_3_label'), icon: 'ri-user-heart-line' },
    { number: t('stat_4_number'), label: t('stat_4_label'), icon: 'ri-emotion-happy-line' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Language Switcher - Fixed Position */}
      <div className="fixed top-6 right-6 z-50">
        <LanguageSwitcher />
      </div>

      {/* Floating Navigation */}
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 hidden lg:block">
        <nav className="bg-white/90 backdrop-blur-md border border-gray-200 rounded-full px-6 py-3 shadow-lg">
          <div className="flex items-center justify-center space-x-8">
            <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer text-sm whitespace-nowrap font-medium">
              {t('nav_home')}
            </a>
            <a href="/about" className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer text-sm whitespace-nowrap font-medium">
              {t('nav_about')}
            </a>
            <a href="/#services" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer text-sm whitespace-nowrap font-medium">
              {t('ai_solutions')}
            </a>
            <a href="/#work" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer text-sm whitespace-nowrap font-medium">
              {t('case_studies')}
            </a>
            <button 
              onClick={() => {
                window.location.href = '/#contact';
              }}
              className="bg-blue-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm shadow-md"
            >
              {t('free_consultation')}
            </button>
          </div>
        </nav>
      </div>

      {/* Hero Section */}
      <header className="relative min-h-[60vh] flex items-center justify-center px-6 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/7907d258e4daf405293babf96afe6b39.jpg" 
            alt={t('nav_about')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full text-center">
          <div className="animate-fade-in-up">
            <h1 className="font-hero text-5xl lg:text-7xl font-black leading-tight mb-6 text-white" style={{ fontWeight: 900 }}>
              {t('about_hero_title_1')}<span className="text-blue-300">{t('about_hero_title_2')}</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              {t('about_hero_description')}
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </header>

      {/* Stats Section */}
      <section 
        id="stats" 
        data-animate 
        className={getAnimationClass('stats', 'px-6 py-16 lg:px-12 bg-white')}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                id={`stat-${index}`}
                data-animate
                className={getAnimationClass(`stat-${index}`, 'text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300')}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-white text-3xl`}></i>
                </div>
                <div className="text-4xl lg:text-5xl font-black text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-700 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company History Timeline */}
      <section 
        id="history" 
        data-animate 
        className={getAnimationClass('history', 'px-6 py-24 lg:px-12 bg-gray-50')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-5xl lg:text-6xl font-bold mb-4 text-gray-800">
              <span className="text-blue-600">{t('footer_company_name')}</span> {t('about_history_title')} <span className="text-blue-600">{t('about_history_highlight')}</span>
            </h2>
            <p className="text-xl text-gray-600 mt-4">{t('about_history_subtitle')}</p>
          </div>

          <div className="relative">
            {/* Timeline Line with Animation */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-blue-400 to-blue-300 transform -translate-x-1/2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
              {/* Animated flowing dots */}
              <div className="absolute w-2 h-2 bg-white rounded-full left-1/2 -translate-x-1/2 animate-flow-down shadow-lg"></div>
              <div className="absolute w-2 h-2 bg-white rounded-full left-1/2 -translate-x-1/2 animate-flow-down-delayed shadow-lg"></div>
            </div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  id={`milestone-${index}`}
                  data-animate
                  className={getAnimationClass(`milestone-${index}`, `relative flex flex-col lg:flex-row items-center ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`)}
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  {/* Content Card with Enhanced Animation */}
                  <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                    <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 group">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 ${
                        milestone.color === 'blue' ? 'bg-blue-100 group-hover:bg-blue-200' :
                        milestone.color === 'teal' ? 'bg-teal-100 group-hover:bg-teal-200' :
                        milestone.color === 'cyan' ? 'bg-cyan-100 group-hover:bg-cyan-200' :
                        milestone.color === 'red' ? 'bg-red-100 group-hover:bg-red-200' :
                        milestone.color === 'orange' ? 'bg-orange-100 group-hover:bg-orange-200' :
                        milestone.color === 'green' ? 'bg-green-100 group-hover:bg-green-200' :
                        'bg-purple-100 group-hover:bg-purple-200'
                      }`}>
                        <i className={`${milestone.icon} text-3xl transition-all duration-500 group-hover:scale-125 ${
                          milestone.color === 'blue' ? 'text-blue-600' :
                          milestone.color === 'teal' ? 'text-teal-600' :
                          milestone.color === 'cyan' ? 'text-cyan-600' :
                          milestone.color === 'red' ? 'text-red-600' :
                          milestone.color === 'orange' ? 'text-orange-600' :
                          milestone.color === 'green' ? 'text-green-600' :
                          'text-purple-600'
                        }`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-2 transition-colors duration-300 group-hover:text-blue-600">{milestone.title}</h3>
                      <p className="text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-gray-700">{milestone.description}</p>
                      
                      {/* Decorative Corner Element */}
                      <div className={`absolute top-4 ${index % 2 === 0 ? 'right-4' : 'left-4'} w-12 h-12 opacity-10 transition-all duration-500 group-hover:opacity-30 group-hover:scale-125 ${
                        milestone.color === 'blue' ? 'text-blue-600' :
                        milestone.color === 'teal' ? 'text-teal-600' :
                        milestone.color === 'cyan' ? 'text-cyan-600' :
                        milestone.color === 'red' ? 'text-red-600' :
                        milestone.color === 'orange' ? 'text-orange-600' :
                        milestone.color === 'green' ? 'text-green-600' :
                        'text-purple-600'
                      }`}>
                        <i className={`${milestone.icon} text-5xl`}></i>
                      </div>
                    </div>
                  </div>

                  {/* Year Badge with Pulse Animation */}
                  <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full items-center justify-center shadow-xl z-10 transition-all duration-500 hover:scale-125 hover:rotate-12 hover:shadow-2xl group cursor-pointer">
                    <span className="text-white font-black text-xl transition-all duration-300 group-hover:scale-110">{milestone.year}</span>
                    {/* Pulse Ring Animation */}
                    <div className="absolute inset-0 rounded-full bg-blue-400 opacity-0 group-hover:opacity-30 group-hover:scale-150 transition-all duration-700"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-blue-400 opacity-0 animate-ping"></div>
                  </div>

                  {/* Mobile Year Badge with Animation */}
                  <div className="lg:hidden w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-xl my-4 transition-all duration-500 hover:scale-110 hover:rotate-12 relative group cursor-pointer">
                    <span className="text-white font-black text-lg transition-all duration-300 group-hover:scale-110">{milestone.year}</span>
                    <div className="absolute inset-0 rounded-full bg-blue-400 opacity-0 group-hover:opacity-30 group-hover:scale-150 transition-all duration-700"></div>
                  </div>

                  {/* Spacer */}
                  <div className="hidden lg:block w-5/12"></div>
                  
                  {/* Connecting Line Animation */}
                  <div className={`hidden lg:block absolute top-1/2 ${index % 2 === 0 ? 'left-1/2 ml-12' : 'right-1/2 mr-12'} w-8 h-0.5 bg-gradient-to-r ${
                    index % 2 === 0 ? 'from-blue-500 to-transparent' : 'from-transparent to-blue-500'
                  } transition-all duration-700 opacity-0 group-hover:opacity-100`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section 
        id="values" 
        data-animate 
        className={getAnimationClass('values', 'px-6 py-24 lg:px-12 bg-white')}
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-5xl lg:text-6xl font-bold mb-4 text-gray-800">
              {t('about_values_title')} <span className="text-blue-600">{t('about_values_highlight')}</span>
            </h2>
            <p className="text-3xl font-bold text-blue-600 mt-6 tracking-wide">{t('about_values_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                id={`value-${index}`}
                data-animate
                className={getAnimationClass(`value-${index}`, `bg-gradient-to-br ${
                  value.color === 'blue' ? 'from-blue-50 to-blue-100' :
                  value.color === 'teal' ? 'from-teal-50 to-teal-100' :
                  value.color === 'cyan' ? 'from-cyan-50 to-cyan-100' :
                  'from-red-50 to-red-100'
                } rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105`)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                  value.color === 'blue' ? 'bg-blue-600' :
                  value.color === 'teal' ? 'bg-teal-600' :
                  value.color === 'cyan' ? 'bg-cyan-600' :
                  'bg-red-600'
                }`}>
                  <i className={`${value.icon} text-white text-4xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{value.title}</h3>
                <p className="text-gray-700 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta" 
        data-animate 
        className={getAnimationClass('cta', 'px-6 py-24 lg:px-12 bg-gradient-to-br from-blue-600 to-blue-800')}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('about_cta_title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            {t('about_cta_description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                window.location.href = '/#contact';
              }}
              className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl text-lg"
            >
              {t('contact_us')}
            </button>
            <button
              onClick={() => {
                window.location.href = '/#services';
              }}
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 whitespace-nowrap cursor-pointer text-lg"
            >
              {t('view_services')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer 
        id="footer" 
        data-animate 
        className={getAnimationClass('footer', 'px-6 py-12 lg:px-12 bg-gray-900 text-white')}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/08bf321c293116922a6a49327bb3deb3/c4869da75471db3607e284c236bcbf64.jpeg" 
                  alt="迎袺智能科技 Logo" 
                  className="h-12 w-auto mr-3"
                />
                <h3 className="text-xl font-bold text-blue-400">{t('footer_company_name')}</h3>
              </div>
              <p className="text-gray-400 mb-4">{t('footer_slogan')}</p>
              <div className="space-y-2">
                <a href="mailto:win.ji.integration@gmail.com" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  <i className="ri-mail-line mr-2"></i>
                  win.ji.integration@gmail.com
                </a>
                <a href="tel:+886986246885" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  <i className="ri-phone-line mr-2"></i>
                  +886-986-246-885
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-lg">{t('footer_quick_links')}</h4>
              <div className="space-y-2">
                <a href="/" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  {t('nav_home')}
                </a>
                <a href="/about" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  {t('nav_about')}
                </a>
                <a href="/#services" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  {t('footer_ai_products')}
                </a>
                <a href="/#work" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
                  {t('footer_projects')}
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-lg">{t('footer_system_services')}</h4>
              <div className="space-y-2">
                <a href="/#service-1" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">{t('footer_ba_system')}</a>
                <a href="/#service-2" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">{t('footer_ca_system')}</a>
                <a href="/#service-3" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">{t('footer_oa_system')}</a>
                <a href="/#service-4" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">{t('footer_sa_system')}</a>
                <a href="/#service-5" className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">{t('footer_fa_system')}</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4 text-lg">{t('footer_follow_us')}</h4>
              <div className="flex space-x-4">
                <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 {t('footer_company_name')}. {t('footer_rights')}
            </p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
